/* The consent screen for a CLI sign-in — shared by the AI21 Gateway and Harness
 * Optimizer CLIs.
 *
 * This click is the entire security boundary. Nothing about a request arriving on a
 * loopback port proves the process listening is the CLI the user just started, so the
 * screen's whole job is to let a person confirm that it is: the CLI printed a short code,
 * this page shows the code it was asked to show, and the copy says to deny on a mismatch.
 *
 * Consequently: an explicit Approve, no auto-submit on mount, no "remember this device",
 * and a plain statement of what is being handed over. The screen never decides *whether*
 * the request is acceptable — CliLoginRoute has already refused anything it will not
 * honour — it only decides whether the user agrees.
 */

import { useState } from "react";
import { logger } from "../services/telemetry";
import { AuthCard, AuthCta, AuthHeading, AuthFrame } from "./AuthShared";
import { currentRefreshToken } from "./firebase";
import { firebaseConfig } from "./config";
import { deliverCliCredential } from "./deliverCliCredential";
import { isFresh } from "./cliLoginRequest";
import { useCliClient } from "./authBrand";
import {
  audienceFrom,
  headingFor,
  REFUSAL_COPY,
  FAILED_COPY,
  NOT_YOURS_COPY,
} from "./cliLoginCopy";
import type { CliLoginRefusal, CliLoginRequest } from "./cliLoginRequest";
import type { AuthUser } from "./identitySession";

type Stage =
  "asking" | "working" | "approved" | "denied" | "failed" | "expired";

/** Terminal state for a request we will not act on. Deliberately says nothing was shared. */
export function CliLoginRefused({
  reason,
}: {
  reason: CliLoginRefusal | "framed";
}) {
  // Read from the URL rather than a parsed request: these states render before — or
  // instead of — a successful parse, so there is nothing else to take the brand from.
  const audience = audienceFrom(useCliClient());

  return (
    <Shell>
      <AuthHeading>Can&apos;t continue</AuthHeading>
      <Body>{REFUSAL_COPY[audience][reason]}</Body>
    </Shell>
  );
}

export function CliLoginScreen({
  request,
  user,
}: {
  request: CliLoginRequest;
  user: AuthUser;
}) {
  const [stage, setStage] = useState<Stage>("asking");
  const audience = audienceFrom(useCliClient());

  async function approve() {
    // Re-checked on the click, not only when the page rendered: this tab can sit open long
    // past the CLI's deadline, and by then the port in `redirectUri` may belong to another
    // process entirely. Checked before the token is even read, so an expired request never
    // has one in hand.
    if (!isFresh(request.expiresAt / 1000)) {
      setStage("expired");
      return;
    }

    setStage("working");
    const refreshToken = await currentRefreshToken();
    if (!refreshToken) {
      // Signed in according to the identity cookie, but Firebase has no user on this
      // page load — so there is no token to give. Re-signing in fixes it.
      logger.warn(
        new Error("no Firebase refresh token available for a CLI login"),
        "CLI sign-in could not be completed",
      );
      setStage("failed");
      return;
    }

    try {
      // The other half of this wire contract is `_REQUIRED_FIELDS` in ai21-hopt-cli
      // `ai21_hopt_cli/login.py`. A TypeScript app and a Python CLI share no schema, so
      // renaming a key here breaks sign-in with nothing failing on this side — check both.
      await deliverCliCredential(request.redirectUri, {
        state: request.state,
        refresh_token: refreshToken,
        firebase_api_key: firebaseConfig.apiKey,
        project_id: firebaseConfig.projectId,
        user_id: user.userId,
        user_email: user.userEmail,
      });
      // Reached only on the fetch path; the form fallback navigates away instead.
      setStage("approved");
    } catch (err) {
      logger.error(
        err instanceof Error ? err : new Error(String(err)),
        "CLI sign-in could not be delivered",
      );
      setStage("failed");
    }
  }

  async function deny() {
    setStage("denied");
    // Best-effort: telling the CLI lets it exit immediately instead of sitting on its
    // deadline. It carries no credential, and a failure here changes nothing.
    try {
      // The other half of this wire contract is `_REQUIRED_FIELDS` in ai21-hopt-cli
      // `ai21_hopt_cli/login.py`. A TypeScript app and a Python CLI share no schema, so
      // renaming a key here breaks sign-in with nothing failing on this side — check both.
      await deliverCliCredential(request.redirectUri, {
        state: request.state,
        error: "access_denied",
      });
    } catch {
      // The CLI will time out on its own.
    }
  }

  if (stage === "approved") {
    return (
      <Shell>
        <AuthHeading>You&apos;re signed in</AuthHeading>
        <Body>You can close this tab and return to your terminal.</Body>
      </Shell>
    );
  }

  if (stage === "denied") {
    return (
      <Shell>
        <AuthHeading>Sign-in denied</AuthHeading>
        <Body>
          Nothing was shared. You can close this tab — if you did not start
          this, nothing further is needed.
        </Body>
      </Shell>
    );
  }

  if (stage === "expired") {
    return <CliLoginRefused reason="expired" />;
  }

  if (stage === "failed") {
    return (
      <Shell>
        <AuthHeading>Couldn&apos;t complete the sign-in</AuthHeading>
        <Body>{FAILED_COPY[audience]}</Body>
      </Shell>
    );
  }

  return (
    <Shell>
      <AuthHeading>{headingFor(audience)}</AuthHeading>

      <Body>
        A command-line tool on this computer is asking to sign in as{" "}
        <strong style={{ color: "var(--fg-1)" }}>{user.userEmail}</strong>.
      </Body>

      <div
        className="flex flex-col items-center gap-1.5 rounded-md px-4 py-3"
        style={{ background: "var(--bg-2)" }}
      >
        <span className="t-body-xs" style={{ color: "var(--fg-3)" }}>
          Your terminal should be showing this code
        </span>
        <span
          data-testid="cli-login-code"
          className="t-h3"
          style={{
            color: "var(--fg-1)",
            fontVariantNumeric: "tabular-nums",
            letterSpacing: "0.12em",
          }}
        >
          {request.code}
        </span>
      </div>

      <Body>
        <strong style={{ color: "var(--fg-1)" }}>
          If your terminal shows a different code, or {NOT_YOURS_COPY[audience]}
          , choose Deny.
        </strong>{" "}
        Approving lets that tool act as you until you sign it out, including
        reading and managing your API keys.
      </Body>

      <div className="flex w-full flex-col gap-2">
        <AuthCta
          className="w-full"
          disabled={stage === "working"}
          onClick={() => void approve()}
        >
          {stage === "working" ? "Signing in…" : "Approve"}
        </AuthCta>
        <button
          type="button"
          className="t-body-sm h-11 w-full rounded-md border transition-colors"
          style={{ borderColor: "var(--border-1)", color: "var(--fg-2)" }}
          disabled={stage === "working"}
          onClick={() => void deny()}
        >
          Deny
        </button>
      </div>
    </Shell>
  );
}

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <AuthFrame>
      <div className="onboarding onboarding-card w-full">
        <AuthCard>
          <div className="flex flex-col items-center gap-5 text-center">
            {children}
          </div>
        </AuthCard>
      </div>
    </AuthFrame>
  );
}

function Body({ children }: { children: React.ReactNode }) {
  return (
    <p className="t-body-sm" style={{ color: "var(--fg-3)" }}>
      {children}
    </p>
  );
}
